import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
  Modal,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  TablePagination,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssignmentIcon from "@mui/icons-material/Assignment";
import katex from "katex";
import "katex/dist/katex.min.css";
import sanitizeHtml from "sanitize-html";

const StudentResults = () => {
  const theme = useTheme();
  const token = sessionStorage.getItem("student_token");
  const [quizHistory, setQuizHistory] = useState([]);
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState("newest");
  const [resultFilter, setResultFilter] = useState("all");
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const renderContent = (html) => {
    if (!html) return null;
    const parts = html.split(/(\$\$.+?\$\$|\$.+?\$)/g);
    return (
      <div>
        {parts.map((part, index) => {
          if (part.startsWith("$$") && part.endsWith("$$")) {
            const latex = part.slice(2, -2);
            try {
              const renderedLatex = katex.renderToString(latex, { displayMode: true });
              return (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content block-latex"
                />
              );
            } catch (error) {
              console.error("Block LaTeX rendering error:", error);
              return <div key={index} className="error-content">{part}</div>;
            }
          }
          if (part.startsWith("$") && part.endsWith("$")) {
            const latex = part.slice(1, -1);
            try {
              const renderedLatex = katex.renderToString(latex, { displayMode: false });
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content inline-latex"
                />
              );
            } catch (error) {
              console.error("Inline LaTeX rendering error:", error);
              return <span key={index} className="error-content">{part}</span>;
            }
          }
          try {
            const formattedContent = part.replace(/\n/g, "<br />");
            const sanitizedContent = sanitizeHtml(formattedContent, {
              allowedTags: ["b", "i", "em", "strong", "span", "br", "u", "sub", "sup", "a", "img"],
              allowedAttributes: {
                img: ["src", "alt"],
                a: ["href", "target"],
                span: ["style"],
              },
              transformTags: {
                p: "span",
                div: "span",
              },
            });
            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                className="rendered-content inline-text"
              />
            );
          } catch (error) {
            console.error("HTML rendering error:", error);
            return <span key={index} className="error-content">{part}</span>;
          }
        })}
      </div>
    );
  };

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchQuizHistory();
  }, [navigate, token]);

  const parseGivenAnswer = (givenAnswer) => {
    if (!givenAnswer) return "Not Attempted";
    const cleaned = givenAnswer.replace(/[\[\]']+/g, '');
    return cleaned || "Not Attempted";
  };

  const handleExportReview = async (quizId, attemptId, quizTitle) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.get(
        `${API_BASE_URL}/student/export-quiz-review/${quizId}/${attemptId}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Quiz_Review_${quizTitle}_Attempt_${attemptId}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error("Error exporting quiz review:", err);
      alert("Failed to export quiz review.");
    }
  };

  const getExplanationForAnswer = (question) => {
    const parsedAnswer = parseGivenAnswer(question.given_answer);
    if (!parsedAnswer || parsedAnswer === "Not Attempted") return null;

    if (question.is_correct) {
      const correctAnswerObj = question.answers.find(ans => ans.is_correct === true);
      return correctAnswerObj?.explanation || null;
    }

    const matchedAnswer = question.answers.find(
      (ans) => ans.answer_text.toLowerCase().trim() === parsedAnswer.toLowerCase().trim()
    );
    return matchedAnswer?.explanation || null;
  };

  const fetchQuizHistory = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await axios.get(`${API_BASE_URL}/quiz-history/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuizHistory(res.data.history || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching quiz history:", err);
      setError("Failed to load history.");
      setLoading(false);
    }
  };

  const fetchReviewQA = async (quizId, attemptId, quizTitle) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const res = await axios.get(
        `${API_BASE_URL}/quiz/${quizId}/review/${attemptId}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviewData({ ...res.data, quizTitle });
      setReviewModalOpen(true);
    } catch (err) {
      console.error("Error fetching review:", err);
      alert("Failed to fetch review.");
    }
  };

  const getDateRange = () => {
    const now = new Date();
    if (dateFilter === "last7days") {
      const date = new Date(now);
      date.setDate(date.getDate() - 7);
      return date;
    }
    if (dateFilter === "last30days") {
      const date = new Date(now);
      date.setDate(date.getDate() - 30);
      return date;
    }
    return null;
  };

  const filteredQuizHistory = quizHistory
    .map((quiz) => ({
      ...quiz,
      attempts: quiz.attempts
        .filter((attempt) => {
          const attemptDate = new Date(attempt.ended_at);
          const dateRange = getDateRange();

          let dateMatch = true;
          if (dateFilter === "last7days" || dateFilter === "last30days") {
            dateMatch = attemptDate >= dateRange;
          }

          const resultMatch = resultFilter === "all" || attempt.result === resultFilter;
          return dateMatch && resultMatch;
        })
        .sort((a, b) => {
          if (dateFilter === "newest" || dateFilter === "all") {
            return new Date(b.ended_at) - new Date(a.ended_at);
          } else if (dateFilter === "oldest") {
            return new Date(a.ended_at) - new Date(b.ended_at);
          } else {
            return new Date(b.ended_at) - new Date(a.ended_at);
          }
        }),
    }))
    .filter((quiz) => quiz.attempts.length > 0);

  if (loading)
    return (
      <Box
        sx={{
          textAlign: "center",
          mt: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        <Typography
          variant="h6"
          mt={2}
          sx={{ color: theme.palette.text.secondary, fontWeight: "600" }}
        >
          Loading your results...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 1200,
        mx: "auto",
        minHeight: "100vh",
        background: theme.palette.background.default,
        borderRadius: "24px",
        // boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: "800",
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          MY RESULTS
        </Typography>
        <Tooltip title="User Guide" arrow>
          <IconButton
            onClick={() => setInfoModalOpen(true)}
            sx={{
              color: "#667eea",
              backgroundColor: "rgba(102, 126, 234, 0.1)",
              "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.2)" },
              p: 1.5,
              ml: 1,
            }}
          >
            <InfoOutlinedIcon sx={{ color: "#3b82f6" }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel sx={{ color: theme.palette.text.primary }}>Date Range</InputLabel>
          <Select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setPage(0);
            }}
            label="Date Range"
            sx={{ color: theme.palette.text.primary }}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="last7days">Last 7 Days</MenuItem>
            <MenuItem value="last30days">Last 30 Days</MenuItem>
            <MenuItem value="newest">Newest First</MenuItem>
            <MenuItem value="oldest">Oldest First</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel sx={{ color: theme.palette.text.primary }}>Result</InputLabel>
          <Select
            value={resultFilter}
            onChange={(e) => {
              setResultFilter(e.target.value);
              setPage(0);
            }}
            label="Result"
            sx={{ color: theme.palette.text.primary }}
          >
            <MenuItem value="all">All Results</MenuItem>
            <MenuItem value="Pass">Pass</MenuItem>
            <MenuItem value="Fail">Fail</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
        {(() => {
          const allAttempts = filteredQuizHistory.flatMap((quiz) =>
            quiz.attempts.map((attempt) => ({
              ...attempt,
              quizTitle: quiz.title,
              quizId: quiz.quiz_id,
            }))
          );

          const sortedAttempts = allAttempts.sort((a, b) => {
            if (dateFilter === "oldest") {
              return new Date(a.ended_at) - new Date(b.ended_at);
            } else {
              return new Date(b.ended_at) - new Date(a.ended_at);
            }
          });

          const paginatedAttempts = sortedAttempts.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
          );

          return (
            <>
              {paginatedAttempts.length > 0 ? (
                paginatedAttempts.map((attempt, idx) => (
                  <Box key={idx} sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        mb: 1,
                        color: theme.palette.text.primary,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        pb: 1,
                      }}
                    >
                      {attempt.quizTitle}
                    </Typography>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 3,
                        mb: 2,
                        border:
                          attempt.result === "Pass"
                            ? "2px solid #4caf50"
                            : "2px solid #f44336",
                        borderRadius: "16px",
                        bgcolor:
                          attempt.result === "Pass"
                            ? theme.palette.mode === 'light' ? "#f1f8f4" : "rgba(76, 175, 80, 0.2)"
                            : theme.palette.mode === 'light' ? "#fef5f5" : "rgba(244, 67, 54, 0.2)",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Chip
                          label={`Attempt #${attempt.attempt_number}`}
                          size="small"
                          sx={{ bgcolor: "#1976d2", color: "white" }}
                        />
                        <Chip
                          label={attempt.result}
                          size="small"
                          sx={{
                            bgcolor:
                              attempt.result === "Pass" ? "#4caf50" : "#f44336",
                            color: "white",
                          }}
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Typography sx={{ color: theme.palette.text.primary }}>
                        <strong>Score:</strong> {attempt.final_score}
                      </Typography>
                      <Typography sx={{ color: theme.palette.text.secondary }}>
                        <strong>Started:</strong> {new Date(attempt.started_at).toLocaleString()}
                      </Typography>
                      <Typography sx={{ color: theme.palette.text.secondary }}>
                        <strong>Ended:</strong> {new Date(attempt.ended_at).toLocaleString()}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={
                            <i className="bi bi-download" style={{ fontSize: "1.2rem" }}></i>
                          }
                          onClick={async () => {
                            await handleExportReview(attempt.quizId, attempt.quiz_attempt_id, attempt.quizTitle);
                          }}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: "600",
                            borderColor: "#667eea",
                            color: "#667eea",
                            "&:hover": {
                              borderColor: "#667eea",
                              background: "rgba(102, 126, 234, 0.08)",
                            },
                          }}
                        >
                          Export
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            fetchReviewQA(
                              attempt.quizId,
                              attempt.quiz_attempt_id,
                              attempt.quizTitle
                            )
                          }
                          sx={{
                            borderRadius: "12px",
                            color: "#667eea",
                            borderColor: "#667eea",
                            "&:hover": {
                              background: "rgba(102, 126, 234, 0.08)",
                              borderColor: "#667eea",
                            },
                          }}
                        >
                          Review QA
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                ))
              ) : (
                <Typography sx={{ color: theme.palette.text.secondary }}>
                  No history available
                </Typography>
              )}
              <TablePagination
                component="div"
                count={sortedAttempts.length}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                //   bgcolor: theme.palette.background.paper,
                }}
              />
            </>
          );
        })()}
      </Box>

      {/* Review Modal */}
<Modal open={reviewModalOpen} onClose={() => setReviewModalOpen(false)}>
  <Box
    sx={{
      p: 0,
      bgcolor: theme.palette.background.paper,
      mx: "auto",
      mt: "3%",
      mb: "3%",
      width: "90%",
      maxWidth: 900,
      maxHeight: "90vh",
      borderRadius: "20px",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      "&::-webkit-scrollbar": {
        width: "10px",
        height: "10px",
      },
      "&::-webkit-scrollbar-track": {
        background: theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9",
        borderRadius: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: theme.palette.mode === "dark" ? "#475569" : "#94a3b8",
        borderRadius: "6px",
        border: `2px solid ${
          theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9"
        }`,
      },
      scrollbarWidth: "thin",
      scrollbarColor:
        theme.palette.mode === "dark"
          ? "#475569 #2d3748"
          : "#94a3b8 #f1f5f9",
    }}
  >
    {/* HEADER */}
    <Box
      sx={{
        p: 3,
        bgcolor:
          theme.palette.mode === "light" ? "#1976d2" : "rgba(25,118,210,0.8)",
        color: "white",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "4px solid #1565c0",
      }}
    >
      <Box>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Review Answers
        </Typography>

        {reviewData && (
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            {reviewData.review?.length || 0} Questions
          </Typography>
        )}
      </Box>
    </Box>

    {/* BODY */}
    <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
      {reviewData?.review?.length > 0 ? (
        reviewData.review.map((q, idx) => {
          const wasAttempted =
            q.given_answer && q.given_answer !== "Not Attempted";

          return (
            <Paper
              key={idx}
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                border: q.is_correct
                  ? "2px solid #4caf50"
                  : wasAttempted
                  ? "2px solid #f44336"
                  : "2px solid #9e9e9e",
                borderRadius: "16px",
                bgcolor: q.is_correct
                  ? theme.palette.mode === "light"
                    ? "#f1f8f4"
                    : "rgba(76,175,80,0.2)"
                  : wasAttempted
                  ? theme.palette.mode === "light"
                    ? "#fef5f5"
                    : "rgba(244,67,54,0.2)"
                  : theme.palette.mode === "light"
                  ? "#fafafa"
                  : "rgba(158,158,158,0.2)",
                transition: "all 0.3s ease",
              }}
            >
              {/* HEADER LINE */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Chip
                      label={`Question ${idx + 1}`}
                      size="small"
                      sx={{
                        bgcolor: "#1976d2",
                        color: "white",
                        fontWeight: "bold",
                        borderRadius: "8px",
                      }}
                    />

                    <Chip
                      label={q.question_type}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: "#1976d2",
                        color: "#1976d2",
                        borderRadius: "8px",
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "600",
                      color: theme.palette.text.primary,
                      mt: 1,
                      lineHeight: 1.6,
                    }}
                  >
                    {renderContent(q.statement)}
                  </Typography>
                </Box>

                <Box>
                  {q.is_correct ? (
                    <CheckCircleIcon sx={{ fontSize: 40, color: "#4caf50" }} />
                  ) : wasAttempted ? (
                    <CancelIcon sx={{ fontSize: 40, color: "#f44336" }} />
                  ) : (
                    <InfoOutlinedIcon
                      sx={{ fontSize: 40, color: "#9e9e9e" }}
                    />
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* USER ANSWER */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: wasAttempted
                    ? q.is_correct
                      ? "#e8f5e9"
                      : "#ffebee"
                    : "#f5f5f5",
                  border: wasAttempted
                    ? q.is_correct
                      ? "1px solid #4caf50"
                      : "1px solid #f44336"
                    : "1px solid #e0e0e0",
                  borderRadius: "12px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      color: wasAttempted
                        ? q.is_correct
                          ? "#2e7d32"
                          : "#c62828"
                        : "#302e2eff",
                    }}
                  >
                    Your Answer:
                  </Typography>

                  {!wasAttempted && (
                    <Chip
                      label="Not Attempted"
                      size="small"
                      sx={{
                        bgcolor: "#9e9e9e",
                        color: "white",
                        borderRadius: "8px",
                      }}
                    />
                  )}
                </Box>

                <Typography
                  component="div"
                  sx={{
                    fontSize: "1rem",
                    color: wasAttempted
                      ? q.is_correct
                        ? "#2e7d32"
                        : "#c62828"
                      :  "#302e2eff",
                    fontWeight: wasAttempted ? "500" : "normal",
                    fontStyle: !wasAttempted ? "italic" : "normal",
                    "& > div": { display: "inline" },
                  }}
                >
                  {renderContent(parseGivenAnswer(q.given_answer))}
                </Typography>
              </Paper>

              {/* CORRECT ANSWER */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: "#e8f5e9",
                  border: "1px solid #4caf50",
                  borderRadius: "12px",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    color: "#2e7d32",
                    mb: 1,
                  }}
                >
                  Correct Answer:
                </Typography>

                <Typography
                  component="div"
                  sx={{
                    fontSize: "1rem",
                    color: "#2e7d32",
                    fontWeight: "500",
                    "& > div": { display: "inline" },
                  }}
                >
                  {Array.isArray(q.correct_answer)
                    ? q.correct_answer.map((ans, i) => (
                        <span key={i}>
                          {renderContent(ans)}
                          {i < q.correct_answer.length - 1 ? ", " : ""}
                        </span>
                      ))
                    : renderContent(q.correct_answer)}
                </Typography>
              </Paper>

              {/* EXPLANATION */}
              {(q.explanation || getExplanationForAnswer(q)) && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mt: 2,
                    bgcolor: "#e3f2fd",
                    border: "1px solid #2196f3",
                    borderRadius: "12px",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      color: "#1565c0",
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                    Explanation:
                  </Typography>

                  <Typography
                    component="div"
                    sx={{
                      fontSize: "0.95rem",
                      color: "#1565c0",
                      lineHeight: 1.6,
                      "& > div": { display: "inline" },
                    }}
                  >
                    {renderContent(
                      getExplanationForAnswer(q) ||
                        q.explanation ||
                        "No explanation provided."
                    )}
                  </Typography>
                </Paper>
              )}
            </Paper>
          );
        })
      ) : (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <Typography variant="h6" color={theme.palette.text.secondary}>
            No review data available
          </Typography>
        </Box>
      )}
    </Box>

    {/* FOOTER */}
    <Box
      sx={{
        p: 2,
        bgcolor:
          theme.palette.mode === "light"
            ? "#f5f5f5"
            : "rgba(55,65,81,0.5)",
        borderTop: `1px solid ${theme.palette.divider}`,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <Button
        variant="contained"
        onClick={() => setReviewModalOpen(false)}
        sx={{
          minWidth: 120,
          borderRadius: "12px",
          padding: "10px 24px",
          fontWeight: "600",
          textTransform: "none",
          background:
            theme.palette.mode === "light"
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
          "&:hover": {
            background:
              theme.palette.mode === "light"
                ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
          },
        }}
      >
        Close
      </Button>
    </Box>
  </Box>
</Modal>


      {/* Info Modal */}
      <Modal open={infoModalOpen} onClose={() => setInfoModalOpen(false)}>
        <Box
          sx={{
            p: 0,
            bgcolor: theme.palette.background.paper,
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 900,
            maxHeight: "90vh",
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",

            "&::-webkit-scrollbar": {
      width: "10px",
      height: "10px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9',
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.mode === 'dark' ? '#475569' : '#94a3b8',
      borderRadius: "6px",
      border: `2px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9'}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.mode === 'dark' ? '#64748b' : '#64748b',
    },
    // Custom scrollbar for Firefox
    scrollbarWidth: "thin",
    scrollbarColor: theme.palette.mode === 'dark' ? '#475569 #2d3748' : '#94a3b8 #f1f5f9',
  }}
          
        >
          <Box
            sx={{
              p: 3.5,
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "700", mb: 0.5 }}>
                User Guide
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                My Results
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 4, overflowY: "auto", flex: 1 }}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Overview
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                This page shows your quiz results and allows you to review your answers.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <AssignmentIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Quiz Results
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                Each quiz result displays your score, status, and allows you to review your answers.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <CheckCircleIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Review QA
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                Click "Review QA" to see your answers, correct answers, and explanations.
              </Typography>
            </Box>

            <Box
              sx={{
                p: 3,
                borderRadius: "12px",
                bgcolor: theme.palette.mode === 'light' ? "#f7fafc" : "rgba(55, 65, 81, 0.5)",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mb: 2 }}>
                💡 Quick Tips
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Use the filters to narrow down results by date or result.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Click "Export" to download your review as an Excel file.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Contact your instructor if you have questions about the review.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              p: 3,
              bgcolor: theme.palette.mode === 'light' ? "#f7fafc" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setInfoModalOpen(false)}
              sx={{
                minWidth: 120,
                borderRadius: "10px",
                padding: "10px 28px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                  boxShadow: "0 6px 16px rgba(102, 126, 234, 0.4)",
                },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default StudentResults;
